import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onFileSelect, isProcessing }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isProcessing) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    if (isProcessing) return;

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      const file = files?.[0];
      if (file?.name?.endsWith('.csv')) {
        onFileSelect(file);
      } else {
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'error',
            message: 'Formato inválido. Por favor, selecione um arquivo CSV.',
            progress: 0,
            details: 'Apenas arquivos .csv são aceitos para importação.'
          }
        }));
      }
    }
  };

  const handleFileInput = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      const file = files?.[0];
      if (file?.name?.endsWith('.csv')) {
        onFileSelect(file);
      } else {
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'error',
            message: 'Formato inválido. Por favor, selecione um arquivo CSV.',
            progress: 0,
            details: 'Apenas arquivos .csv são aceitos para importação.'
          }
        }));
      }
    }
  };

  const handleBrowseClick = () => {
    if (!isProcessing) {
      fileInputRef?.current?.click();
    }
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`
        relative border-2 border-dashed rounded-lg transition-smooth
        ${isDragging 
          ? 'border-primary bg-primary/5 scale-[1.02]' 
          : 'border-border bg-card hover:border-primary/50'
        }
        ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      onClick={handleBrowseClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileInput}
        className="hidden"
        disabled={isProcessing}
      />
      <div className="p-8 md:p-12 lg:p-16 text-center">
        <div className="flex justify-center mb-6">
          <div className={`
            flex items-center justify-center w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 
            rounded-full transition-smooth
            ${isDragging ? 'bg-primary/20 scale-110' : 'bg-primary/10'}
          `}>
            <Icon 
              name={isDragging ? 'Download' : 'Upload'} 
              size={32} 
              color="var(--color-primary)"
              className={isDragging ? 'animate-bounce' : ''}
            />
          </div>
        </div>

        <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-3">
          {isDragging ? 'Solte o arquivo aqui' : 'Arraste seu arquivo CSV'}
        </h3>

        <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
          ou clique para selecionar do seu computador
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <Button
            variant="default"
            size="lg"
            iconName="FileUp"
            iconPosition="left"
            disabled={isProcessing}
            onClick={(e) => {
              e?.stopPropagation();
              handleBrowseClick();
            }}
          >
            Selecionar Arquivo
          </Button>

          <Button
            variant="outline"
            size="lg"
            iconName="Download"
            iconPosition="left"
            disabled={isProcessing}
            onClick={(e) => {
              e?.stopPropagation();
              const link = document.createElement('a');
              link.href = '/assets/template_wedrop.csv';
              link.download = 'template_wedrop.csv';
              link?.click();
            }}
          >
            Baixar Template
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 text-xs md:text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={16} />
            <span>Formato: CSV</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="HardDrive" size={16} />
            <span>Sem limite de tamanho</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Package" size={16} />
            <span>Produtos ilimitados</span>
          </div>
        </div>
      </div>
      <div className="hidden lg:block absolute bottom-4 left-4 right-4">
        <div className="bg-muted/50 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="caption text-muted-foreground">
              <strong className="text-foreground">Dica:</strong> Certifique-se de que seu CSV contém as colunas: Nome, SKU, Preço, Custo, Categoria, Peso, Dimensões e URLs de imagens.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadZone;